import React, { useState } from "react"
import { LocalizedStrict } from "../../../../../../localization/components/AppLocalized"
import { Box, Skeleton, Typography } from "@mui/material"
import { useApiQuery } from "../../../../../../../core/api/useApiQuery"
import { fetchKitchenNews, KitchenNewsResponse, UserWeekMenu } from "../../../weekMenuApi"
import { useAppSelector } from "../../../../../../store/storeHooks"
import { selectKitchen } from "../../../../../nonAdminUserSelectors"
import { NewsModal } from "./NewsModal"
import { KitchenNews } from "../../../weekMenuTypes"
import { NewsCard } from "./NewsCard"
import { makeStyles } from "tss-react/mui"
import { ErrorBoundary } from "react-error-boundary"
import { UserDashboardErrorFallbackCard } from "../../UserDashboardErrorFallbackCard"
import { QueryErrorBoundary } from "../../../../../../../core/api/components/QueryErrorBoundary"
import { useAppLocalization } from "../../../../../../localization/components/AppLocalizationProvider"
import { RefetchIntervals } from "../../../../../../../core/api/refetchIntervals"

const transformKitchenData = (data: KitchenNewsResponse) => {
    return data.news
}

const useStyles = makeStyles()((theme) => ({
    container: {
        padding: "1.25rem",
        borderRadius: ".75rem",
        backgroundColor: "white",
        marginBottom: "3rem",
        [theme.breakpoints.up("xl")]: {
            padding: "2.25rem",
        },
    },
    newsGrid: {
        display: "grid",
        gridTemplateColumns: "repeat(3, 1fr)",
        // gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))",
        gap: "1.25rem",
    },
    skeletonCard: {
        height: "8rem",
        borderRadius: ".75rem",
        width: "100%",
    },
    errorCard: {
        backgroundColor: "transparent",
        boxShadow: "none",
    },
    errorContent: {
        paddingTop: 0,
        paddingLeft: 0,
        paddingRight: 0,
    },
}))

export function SafeNewsSection() {
    const { classes } = useStyles()

    const selectedKitchen = useAppSelector(selectKitchen)
    const [showNewsModal, setShowNewsModal] = useState(false)
    const [selectedNews, setSelectedNews] = useState<KitchenNews | null>()

    const { data: kitchenNews, isLoading } = useApiQuery(fetchKitchenNews, {
        queryName: UserWeekMenu.KITCHEN_NEWS,
        dependencies: {
            locationId: selectedKitchen?.id ?? 0,
        },
        enabled: Boolean(selectedKitchen?.id),
        select: transformKitchenData,
        useErrorBoundary: true,
        refetchInterval: RefetchIntervals.medium,
    })

    const hasNews = Array.isArray(kitchenNews) && kitchenNews.length > 0

    if (!hasNews) {
        return null
    }

    return (
        <Box className={classes.container}>
            <LocalizedStrict id="user-home-canteen-news">
                <Typography variant="h6" marginBottom="1rem" fontWeight="bold">
                    News
                </Typography>
            </LocalizedStrict>
            <Box className={classes.newsGrid}>
                {isLoading ? (
                    <>
                        <Skeleton variant="rectangular" className={classes.skeletonCard} />
                        <Skeleton variant="rectangular" className={classes.skeletonCard} />
                        <Skeleton variant="rectangular" className={classes.skeletonCard} />
                    </>
                ) : (
                    <ErrorBoundary fallback={<UserDashboardErrorFallbackCard />}>
                        <>
                            {hasNews &&
                                kitchenNews?.map((news: KitchenNews) => (
                                    <NewsCard
                                        key={news.id}
                                        newsItem={news}
                                        onClick={(news) => {
                                            setSelectedNews(news)
                                            setShowNewsModal(true)
                                        }}
                                    />
                                ))}
                        </>
                    </ErrorBoundary>
                )}
            </Box>
            {showNewsModal && selectedNews && (
                <NewsModal
                    isOpen={showNewsModal}
                    onClose={() => {
                        setShowNewsModal(false)
                        setSelectedNews(null)
                    }}
                    news={selectedNews}
                />
            )}
        </Box>
    )
}

export function NewsSection() {
    return (
        <QueryErrorBoundary Fallback={NewsFallback}>
            <SafeNewsSection />
        </QueryErrorBoundary>
    )
}

const NewsFallback = () => {
    const { classes } = useStyles()
    const { l10n } = useAppLocalization()

    return (
        <Box marginBottom="3rem">
            <Box className={classes.container}>
                <UserDashboardErrorFallbackCard
                    classNames={classes.errorCard}
                    contentClassNames={classes.errorContent}
                    errorMessage={l10n.getString("user-dashboard-error-news-menu-unavailable")}
                >
                    <LocalizedStrict id="user-home-canteen-news">
                        <Typography variant="h6" marginBottom="1rem" fontWeight="bold">
                            News
                        </Typography>
                    </LocalizedStrict>
                </UserDashboardErrorFallbackCard>
            </Box>
        </Box>
    )
}
